import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Heart } from 'lucide-react';
import { LikeService } from '../../services/LikeService';

interface LikeCounterProps {
  className?: string;
}

interface InteractionDateDetails {
  day: number;
  month: number;
  year: number;
  timestamp: string;
}

export const LikeCounter = ({ className = '' }: LikeCounterProps) => {
  const [counts, setCounts] = useState({ stars: 0, likes: 0 });
  const [lastInteraction, setLastInteraction] =
    useState<InteractionDateDetails | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [interactionType, setInteractionType] = useState<
    'star' | 'like' | null
  >(null);

  useEffect(() => {
    const unsubscribeCounts = LikeService.getInteractionCounts(setCounts);
    const unsubscribeLastInteraction =
      LikeService.getLastInteraction(setLastInteraction);

    return () => {
      unsubscribeCounts();
      unsubscribeLastInteraction();
    };
  }, []);

  const handleInteraction = async (type: 'star' | 'like') => {
    try {
      setIsAnimating(true);
      setInteractionType(type);
      await LikeService.addInteraction(type);

      setTimeout(() => {
        setIsAnimating(false);
        setInteractionType(null);
      }, 1000);
    } catch (error) {
      console.error(`Error adding ${type}:`, error);
      setIsAnimating(false);
      setInteractionType(null);
    }
  };

  const formatDateTime = (details: InteractionDateDetails | null) => {
    if (!details || !details.timestamp) return 'No interactions yet';

    try {
      const date = new Date(details.timestamp);
      if (isNaN(date.getTime())) return 'Invalid date';

      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];

      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;

      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} at ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date format';
    }
  };

  // Add this for debugging
  useEffect(() => {
    console.log('Last Interaction Data:', lastInteraction);
  }, [lastInteraction]);

  return (
    <motion.div
      className={`flex items-center gap-4 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Star Button */}
      <motion.button
        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 
                   rounded-full shadow-sm hover:shadow-md transition-all duration-300
                   text-gray-700 dark:text-gray-300 hover:bg-yellow-50 dark:hover:bg-gray-700"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => handleInteraction('star')}
      >
        <AnimatePresence>
          {isAnimating && interactionType === 'star' ? (
            <motion.div
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.5, 1], rotate: [0, 180, 360] }}
              exit={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            </motion.div>
          ) : (
            <Star className="h-5 w-5 text-yellow-400" />
          )}
        </AnimatePresence>
        <span className="font-medium">{counts.stars}</span>
      </motion.button>

      {/* Like Button */}
      <motion.button
        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 
                   rounded-full shadow-sm hover:shadow-md transition-all duration-300
                   text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-gray-700"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => handleInteraction('like')}
      >
        <AnimatePresence>
          {isAnimating && interactionType === 'like' ? (
            <motion.div
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.5, 1], y: [0, -10, 0] }}
              exit={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Heart className="h-5 w-5 fill-red-500 text-red-500" />
            </motion.div>
          ) : (
            <Heart className="h-5 w-5 text-red-500" />
          )}
        </AnimatePresence>
        <span className="font-medium">{counts.likes}</span>
      </motion.button>

      {/* Last Interaction Time with Error Handling */}
      <motion.div
        className="hidden lg:block md:block text-sm text-gray-500 dark:text-gray-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        Last interaction: {formatDateTime(lastInteraction)}
      </motion.div>
    </motion.div>
  );
};
